import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { HiLocationMarker, HiMail } from 'react-icons/hi';
import {
  FaWhatsapp,
  FaInstagram,
  FaFacebook,
  FaLinkedin,
} from 'react-icons/fa';
import logo from '../assets/images/logo.svg';
import thuiswinkelLogo from '../assets/images/Photoroom.svg';
import idealLogo from '../assets/images/Ideal.svg';
import iso9001Logo from '../assets/images/Layer.svg';
import iso27001Logo from '../assets/images/ISO.svg';
import { createSubscription } from '../services/subscription';

function Footer({ className = '', containerClassName = '' }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const onSubscribe = async () => {
    setMessage('');
    const trimmed = (email || '').trim();
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!isValid) {
      setMessage('Please enter a valid email.');
      return;
    }
    try {
      setLoading(true);
      await createSubscription(trimmed);
      setMessage('Subscribed!');
      setEmail('');
    } catch (err) {
      setMessage('Subscription failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      className={['w-full font-kumbh', className].join(' ').trim()}
      style={{ backgroundColor: '#F9F5F2' }}
    >
      <div
        className={[
          'mx-auto w-full max-w-[1920px] px-4 sm:px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-40 py-16',
          containerClassName,
        ]
          .join(' ')
          .trim()}
      >
        <div className="flex flex-col md:flex-row gap-10 md:gap-16">
          <div className="flex-1 space-y-8">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Biowell" className="h-12 w-auto" />
            </div>
            <p className="text-gray-600 text-sm leading-relaxed max-w-sm">
              At BioWell, we believe optimal health is the foundation for a
              powerful life. That&apos;s why we combine smart blood testing with
              personalized supplements - so you can feel stronger, clearer, and
              more in control.
            </p>
          </div>

          <div className="flex-1 flex flex-col md:flex-row gap-12">
            <div className="flex-1 space-y-5">
              <h3 className="text-gray-800 font-medium text-lg">Contact</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <HiLocationMarker className="h-5 w-5 text-[#119975]" />
                  <span className="text-gray-600 text-sm whitespace-nowrap">
                    BioWell Nederland
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <HiMail className="h-5 w-5 text-[#119975]" />
                  <span className="text-gray-600 text-sm">
                    info@biowell.com
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <FaWhatsapp className="h-5 w-5 text-[#119975]" />
                  <div className="text-sm">
                    <div className="text-gray-600">WhatsApp:</div>
                    <a
                      href="https://wa.me/31302072272"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#119975]"
                    >
                      Stuur een bericht
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 space-y-4 md:space-y-6">
              <h3 className="text-gray-800 text-lg font-medium">
                Stay updated with our latest news!
              </h3>
              <div className="relative w-full sm:max-w-md md:w-96">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 pr-28 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSubscribe();
                  }}
                />
                <button
                  className="absolute right-1 top-1 bottom-1 text-white px-6 py-2 rounded-full text-sm font-medium hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#119975' }}
                  onClick={onSubscribe}
                  disabled={loading}
                >
                  {loading ? 'Subscribing...' : 'Subscribe'}
                </button>
              </div>
              {message ? (
                <div
                  className="text-sm"
                  style={{
                    color: message.includes('failed') ? '#b91c1c' : '#119975',
                  }}
                >
                  {message}
                </div>
              ) : null}
              <div className="flex gap-3">
                <a
                  href="https://www.instagram.com/biowell.health"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
                  style={{ backgroundColor: '#E8F5E8' }}
                >
                  <FaInstagram
                    className="h-4 w-4 sm:h-5 sm:w-5"
                    style={{ color: '#119975' }}
                  />
                </a>
                <a
                  href="https://www.facebook.com/biowellnl"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(
                      'https://www.facebook.com/biowellnl',
                      '_blank',
                      'noopener'
                    );
                  }}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
                  style={{ backgroundColor: '#E8F5E8' }}
                >
                  <FaFacebook
                    className="h-4 w-4 sm:h-5 sm:w-5"
                    style={{ color: '#119975' }}
                  />
                </a>
                <a
                  href="https://www.linkedin.com/company/biowellnl"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(
                      'https://www.linkedin.com/company/biowellnl',
                      '_blank',
                      'noopener'
                    );
                  }}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
                  style={{ backgroundColor: '#E8F5E8' }}
                >
                  <FaLinkedin
                    className="h-4 w-4 sm:h-5 sm:w-5"
                    style={{ color: '#119975' }}
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="border-t border-gray-200"
        style={{ backgroundColor: '#EFE9E5' }}
      >
        <div className="mx-auto w-full max-w-[1920px] px-4 sm:px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-40 py-6 space-y-4 md:space-y-0 md:flex md:items-center md:justify-between">
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-10 md:justify-start">
            <img
              src={thuiswinkelLogo}
              alt="Thuiswinkel Waarborg"
              className="h-9 w-auto sm:h-10"
            />
            <img src={idealLogo} alt="iDEAL" className="h-9 w-auto sm:h-10" />
            <img
              src={iso9001Logo}
              alt="ISO 9001 Certified Company"
              className="h-9 w-auto sm:h-10"
            />
            <img
              src={iso27001Logo}
              alt="ISO 27001"
              className="h-9 w-auto sm:h-10"
            />
          </div>
          <div className="mt-4 md:mt-0 flex items-center justify-center md:justify-end gap-x-3 sm:gap-x-5 text-[12px] sm:text-[13px] text-gray-700 text-center flex-nowrap">
            <div className="flex items-center justify-center gap-x-3 sm:gap-x-5 flex-nowrap whitespace-nowrap">
              <a
                href="#"
                className="hover:text-gray-900 transition-colors whitespace-nowrap"
              >
                Veelgestelde vragen
              </a>
              <span className="text-gray-400">|</span>
              <a
                href="#"
                className="hover:text-gray-900 transition-colors whitespace-nowrap"
              >
                Privacybeleid
              </a>
              <span className="text-gray-400">|</span>
              <a
                href="#"
                className="hover:text-gray-900 transition-colors whitespace-nowrap"
              >
                Abonnementen
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Hidden images for SEO/preloading */}
      <img src="/Biowell_logo.png" alt="Biowell" className="hidden" />
      <img src="/QV_logo_H.webp" alt="Qualevita" className="hidden" />
      <img src="/LCS_logo.png" alt="LCS" className="hidden" />
    </footer>
  );
}

Footer.propTypes = {
  className: PropTypes.string,
  containerClassName: PropTypes.string,
};

export default Footer;
